import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import SectionHeaderView from '../components/views/SectionHeaderView'
import FormInput from '../components/inputs/FormInput'
import FormInput2 from '../components/inputs/FormInput2'
import DropdownInput from '../components/inputs/DropdownInput'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import newBoat from '../assets/new-boat.png'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const EditBoat = () => {
    const { id } = useParams()

    const [usageTypes, setUsageTypes] = useState([])
    const [harbors, setHarbors] = useState([])
    
    const [boatName, setBoatName] = useState('')
    const [boatNameError, setBoatNameError] = useState(null)

    const [registration, setRegistration] = useState('')
    const [registrationError, setRegistrationError] = useState(null)

    const [grossTonnage, setGrossTonnage] = useState('')
    const [grossTonnageError, setGrossTonnageError] = useState(null)

    const [length, setLength] = useState('')
    const [lengthError, setLengthError] = useState(null)

    const [selectedUsageType, setSelectedUsageType] = useState(null)
    const [selectedUsageTypeError, setSelectedUsageTypeError] = useState(null)

    const [selectedHarbor, setSelectedHarbor] = useState(null)
    const [selectedHarborError, setSelectedHarborError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.CLIENT) {
                navigate('/')
            } else {
                getDropdowns()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get dropdowns request
    const getDropdowns = () => {
        fetch('/api/Boat/Dropdowns', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                const arrayUsages = jsonRes.usageTypes.map((option) => ({value: option.id, label: option.name }))
                const arrayHarbors = jsonRes.harbors.map((option) => ({value: option.id, label: option.name }))

                setUsageTypes(arrayUsages)
                setHarbors(arrayHarbors)

                getBoatDetails(arrayUsages, arrayHarbors)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setUsageTypes([])
                    setHarbors([])
                }, 1000)
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setUsageTypes([])
                setHarbors([])
            }, 1000)   
        })
    }

    // Handle get boat details
    const getBoatDetails = (arrayUsages, arrayHarbors) => {
        fetch(`/api/Boat/${id}/Details`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
    
                    setBoatName(jsonRes.name)
                    setRegistration(jsonRes.registration)
                    setGrossTonnage(jsonRes.grossTonnage.toString())
                    setLength(jsonRes.length.toString())
                    setSelectedUsageType(arrayUsages.find((usageType) => usageType.value === jsonRes.usageTypeId))
                    setSelectedHarbor(arrayHarbors.find((harbor) => harbor.value === jsonRes.harborId))
                } else {
                    showErrorToast(strings.getBoatDetailsError)
                    resetVariables()
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                resetVariables()
            }, 1000)  
        })
    }

    // Handle changes in boat name input text
    const onChangeBoatName = (boatNameText) => {
        setBoatNameError(null)
        setBoatName(boatNameText.target.value)
    }

    // Handle changes in registration input text
    const onChangeRegistration = (registrationText) => {
        setRegistrationError(null)
        setRegistration(registrationText.target.value)
    }

    // Handle changes in gross tonnage input text
    const onChangeGrossTonnage = (grossTonnageText) => {
        setGrossTonnageError(null)
        setGrossTonnage(grossTonnageText.target.value)
    }

    // Handle changes in length input text
    const onChangeLength = (lengthText) => {
        setLengthError(null)
        setLength(lengthText.target.value)
    }

    // Handle changes in usage type dropdown
    const onChangeSelectedUsageType = (item) => {
        setSelectedUsageType(item)
        setSelectedUsageTypeError(null)
    }

    // Handle changes in harbor dropdown
    const onChangeSelectedHarbor = (item) => {
        setSelectedHarbor(item)
        setSelectedHarborError(null)
    }

    // Update boat and navigate back to Boats list
    const updateBoat = () => {
        let hasErrors = false

        if (boatName.trim().length === 0) {
            setBoatNameError(strings.insertBoatNameError)
            hasErrors = true
        }
    
        if (registration.trim().length === 0) {
            hasErrors = true
            setRegistrationError(strings.intertRegistrationError)
        }
    
        if (grossTonnage.trim().length === 0) {
            hasErrors = true
            setGrossTonnageError(strings.insertGrossTonnageError)
        }
    
        if (length.trim().length === 0) {
            hasErrors = true
            setLengthError(strings.insertOutOutLengthError)
        }

        if(!selectedUsageType) {
            hasErrors = true
            setSelectedUsageTypeError(strings.selectTypeOfUseError)
        }

        if(!selectedHarbor) {
            hasErrors = true
            setSelectedHarborError(strings.selectAffectedShipyardError)
        }

        if (!hasErrors) {
            setIsLoading(true)

            const data = { 
                Name: boatName,
                Registration: registration,
                GrossTonnage: grossTonnage,
                Length: length,
                UsageTypeId: selectedUsageType.value,
                HarborId: selectedHarbor.value
            }

            // Update boat
            fetch(`/api/Boat/${id}/Edit`, {
                method:'PUT',
                headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json; charset=UTF-8',
                  'Authorization': 'Bearer ' + loggedUser.jwtToken
                }, 
                mode: 'cors',
                body: JSON.stringify(data)
            }).then(async response => {
                setTimeout(() => {
                    setIsLoading(false)

                    if (response.status === 200) {
                        navigate(-1)
                    } else {
                        showErrorToast(strings.updateBoatError)
                    }
                }, 1000) 
        
            }).catch((error) => {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(error)
                }, 1000) 
            })
        }
    }

    // Resets local state variables
    const resetVariables = () => {
        setBoatName('')
        setRegistration('')
        setGrossTonnage('')
        setLength('')
        setSelectedUsageType(null)
        setSelectedHarbor(null)
    } 

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            { isLoading ?
                <div className='h-screen'>
                    <LoadingModal />
                </div>
            :   <div className='flex flex-row'>
                    
                    <div className='flex flex-col flex-1 mb-6'>

                        <div className='flex flex-row items-center mt-16 mb-10 px-[3.5rem] space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.updateBoat}</p>
                        </div>

                        <div className='px-[3.5rem]'>
                            <SectionHeaderView 
                                text={strings.info}
                                margin={16}
                            />
                        </div>

                        <div className='flex flex-1 flex-col mt-4 px-[3.5rem]'>

                            <label htmlFor='boatName' className='font-montserrat font-semibold text-sm'>{strings.boatName}</label>
                            <FormInput 
                                id='boatName' 
                                type='text'
                                value={boatName}
                                placeholder={strings.insertBoatName}
                                onChange={onChangeBoatName}
                                error={boatNameError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{boatNameError ? boatNameError : ''}</p>

                            <label htmlFor='registration' className='font-montserrat font-semibold text-sm mt-9'>{strings.registration}</label>
                            <FormInput 
                                id='registration' 
                                type='text'
                                value={registration}
                                placeholder={strings.intertRegistration}
                                onChange={onChangeRegistration}
                                error={registrationError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{registrationError ? registrationError : ''}</p>

                            <label htmlFor='grossTonnage' className='font-montserrat font-semibold text-sm mt-9'>{strings.grossTonnage}</label>
                            <FormInput2
                                id='grossTonnage' 
                                type='text'
                                value={grossTonnage}
                                placeholder={strings.insertGrossTonnage}
                                onChange={onChangeGrossTonnage}
                                error={grossTonnageError}
                                text={strings.grt}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{grossTonnageError ? grossTonnageError : ''}</p>
                            
                            <label htmlFor='length' className='font-montserrat font-semibold text-sm mt-9'>{strings.outOutLength}</label>
                            <FormInput2
                                id='length' 
                                type='text'
                                value={length}
                                placeholder={strings.insertOutOutLength}
                                onChange={onChangeLength}
                                error={lengthError}
                                text={strings.m}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{lengthError ? lengthError : ''}</p>

                            <p className='font-montserrat font-semibold text-sm mt-9'>{strings.typeOfUse}</p>
                            <DropdownInput 
                                options={usageTypes}
                                value={selectedUsageType}
                                onChange={onChangeSelectedUsageType}
                                error={selectedUsageTypeError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedUsageTypeError ? selectedUsageTypeError : ''}</p>

                            <p className='font-montserrat font-semibold text-sm mt-9'>{strings.affectedShipyard}</p>
                            <DropdownInput 
                                options={harbors}
                                value={selectedHarbor}
                                onChange={onChangeSelectedHarbor}
                                error={selectedHarborError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedHarborError ? selectedHarborError : ''}</p>

                            <div className='flex flex-row items-center mt-9 space-x-6'>
                                <div className='flex w-[16.25rem]'>
                                    <RoundedButtonLink 
                                        path='/boats'
                                        text={strings.cancel}
                                        height={48}
                                        bgColor='#E85F5C'
                                        flex={true}
                                    />
                                </div>

                                <div className='flex w-[16.25rem]'>
                                    <RoundedButton
                                        onClick={updateBoat}
                                        text={strings.save}
                                        height={48}
                                        bgColor='#007EB4'
                                    />
                                </div>
                            
                            </div>

                        </div>

                    </div>

                    <div className='flex flex-1'>
                        <img className='min-h-screen object-cover' src={newBoat} alt='Novo Agendamento'/>
                    </div>
                </div>
            }
                
            <ToastContainer />
        </>
    )
}

export default EditBoat